import React, { useEffect, useMemo } from 'react';
import { CreateUserFormProps, ICreateUserForm } from './types';
import { ArcButton, ArcCheckbox, ArcDialog, ArcMenuItem, ArcOutlinedInput, ArcOutlinedSelect, ArcPasswordInput, ArcStack, ArcText } from '@src/core/ui';
import { useDisclosure } from '@src/core/hooks';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { EMPTY_VALUE, REQUIRED_VALIDATION_TEXT } from '@src/core/config';
import { useCreateUserMutation, useGetUserRolesQuery } from '@src/features/settings/redux';
import { toast } from 'react-toastify';
import { trackError, toTrackedError } from '@src/core/sentry';
import { useGetUserDataQuery, UserEntity, UserRoleType } from '@src/core/features';

// Email: Latin letters, digits, dot and @ only (no live format error, just character filtering).
const EMAIL_STRIP = /[^A-Za-z0-9.@]/g;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_STRIP = /[^0-9+()\s-]/g;
const PHONE_MAX_LENGTH = 15;
// Zip: digits only.
const ZIP_STRIP = /[^0-9]/g;
const ZIP_MAX_LENGTH = 10;
// City: letters only (spaces, hyphens and apostrophes kept for names like "New York", "Saint-Louis").
const CITY_PATTERN = /^[A-Za-z\s'-]+$/;
const CITY_STRIP = /[^A-Za-z\s'-]/g;
const CITY_MAX_LENGTH = 50;

const CreateUserForm: React.FC<CreateUserFormProps> = () => {
  const { open, hide, show } = useDisclosure();

  const { data = {} as UserEntity } = useGetUserDataQuery();
  const { data: roles = [] } = useGetUserRolesQuery();

  const userRole = data.role;
  const [createUser, { isLoading: isSubmitting }] = useCreateUserMutation();


  const { control, register, handleSubmit, watch, setValue, formState: { errors } } = useForm<ICreateUserForm>({ 
    defaultValues: {
      email: EMPTY_VALUE,
      firstname: EMPTY_VALUE,
      lastname: EMPTY_VALUE,
      password: EMPTY_VALUE,
      password2: EMPTY_VALUE,
      phone: EMPTY_VALUE,
      role: EMPTY_VALUE,
      zip: EMPTY_VALUE,
      city: EMPTY_VALUE,
      transferOwnership: false,
    }
  });

  const withFilter = (reg: ReturnType<typeof register>, strip: RegExp, max?: number) => ({
    ...reg,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      const cleaned = event.target.value.replace(strip, '');
      event.target.value = max ? cleaned.slice(0, max) : cleaned;
      return reg.onChange(event);
    },
  });

  const assignableRoles = useMemo(
    () =>
      roles.filter((role) => {
        if (role === UserRoleType.SUPERADMIN) return false;
        if (role === UserRoleType.OWNER) return false;
        return true;
      }),
    [roles]
  );

  const selectedRole = watch('role');
  const isOwnerRole = userRole === UserRoleType.OWNER;

  useEffect(() => {
    if (selectedRole !== UserRoleType.ADMIN) {
      setValue('transferOwnership', false);
    }
  }, [selectedRole, setValue]);

  const showTransferOwnership = isOwnerRole && selectedRole === UserRoleType.ADMIN;

  const onCreateUser: SubmitHandler<ICreateUserForm> = (form) => {
    const payload: ICreateUserForm = {
      ...form,
      transferOwnership:
        isOwnerRole && form.role === UserRoleType.ADMIN ? Boolean(form.transferOwnership) : false,
    };
    createUser(payload).unwrap().then(() => {
      hide();
      toast.success('SUCCESS');
    }).catch((error: unknown) => {
      console.error('Create user error:', error);
      trackError(toTrackedError(error), { code: 'FE_0018', extra: { action: 'createUser' } });
      toast.error('Failed to create user');
    });
  };

  return (
    <React.Fragment>
      <ArcStack direction='row' justifyContent='end'>
        <ArcButton.brand 
          onClick={show} 
          sx={{ minWidth: 200 }}> 
          Create user 
        </ArcButton.brand>
      </ArcStack>
      <ArcDialog open={open} onClose={hide}>
        <ArcStack spacing={2} component='form' onSubmit={handleSubmit(onCreateUser)}>
          <ArcOutlinedInput 
            label='Email' 
            autoComplete='off'
            error={Boolean(errors.email)}
            helperText={errors.email?.message}
            {...withFilter(
              register('email', { 
                required: REQUIRED_VALIDATION_TEXT,
                pattern: {
                  value: EMAIL_PATTERN,
                  message: 'Invalid email format',
                },
              }),
              EMAIL_STRIP,
            )} />
          <ArcStack spacing={2} direction='row'> 
            <ArcOutlinedInput 
              label='First Name'
              autoComplete='off'
              error={Boolean(errors.firstname)}
              helperText={errors.firstname?.message}
              {...register('firstname', 
                { required: REQUIRED_VALIDATION_TEXT }
              )} />
            <ArcOutlinedInput 
              label='Last Name' 
              autoComplete='off'
              error={Boolean(errors.lastname)}
              helperText={errors.lastname?.message}
              {...register('lastname', 
                { required: REQUIRED_VALIDATION_TEXT }
              )}  />
          </ArcStack>
          <Controller 
            name='role' 
            control={control} 
            rules={{ required: REQUIRED_VALIDATION_TEXT }}
            render={({ field: { value, onChange } }) => (
              <ArcOutlinedSelect 
                label='Role'
                value={value} 
                onChange={onChange}
                error={Boolean(errors.role)}
                helperText={errors.role?.message}
              >
                {assignableRoles.map((role) => (
                  <ArcMenuItem key={role} value={role}>
                    {role}
                  </ArcMenuItem>
                ))}
              </ArcOutlinedSelect>
            )}/>
          
          <ArcPasswordInput 
            label='Password' 
            autoComplete='off'
            error={Boolean(errors.password)}
            helperText={errors.password?.message}
            {...register('password', 
              { required: REQUIRED_VALIDATION_TEXT }
            )} />
          <ArcPasswordInput 
            label='Re-enter Password' 
            autoComplete='off'
            error={Boolean(errors.password2)}
            helperText={errors.password2?.message}
            {...register('password2', 
              { required: REQUIRED_VALIDATION_TEXT }
            )} />
          <ArcOutlinedInput 
            label='Phone Number' 
            autoComplete='off'
            error={Boolean(errors.phone)}
            helperText={errors.phone?.message}
            {...withFilter(
              register('phone', { required: REQUIRED_VALIDATION_TEXT }),
              PHONE_STRIP,
              PHONE_MAX_LENGTH,
            )} />
          <ArcOutlinedInput 
            label='Zip Code' 
            autoComplete='off'
            error={Boolean(errors.zip)}
            helperText={errors.zip?.message}
            {...withFilter(
              register('zip', { required: REQUIRED_VALIDATION_TEXT }),
              ZIP_STRIP,
              ZIP_MAX_LENGTH,
            )} />
          <ArcOutlinedInput 
            label='City' 
            autoComplete='off'
            error={Boolean(errors.city)}
            helperText={errors.city?.message}
            {...withFilter(
              register('city', {
                required: REQUIRED_VALIDATION_TEXT,
                maxLength: { value: CITY_MAX_LENGTH, message: `Maximum ${CITY_MAX_LENGTH} characters allowed` },
                pattern: { value: CITY_PATTERN, message: 'Only letters are allowed' },
              }),
              CITY_STRIP,
              CITY_MAX_LENGTH,
            )} />
          
          {showTransferOwnership && (
            <ArcStack direction='row' justifyContent='space-between' alignItems='center' spacing={1}>
              <ArcText.h5>Transfer ownership to this admin</ArcText.h5>
              <Controller
                name='transferOwnership'
                control={control}
                render={({ field: { value, onChange } }) => (
                  <ArcCheckbox
                    color='brand'
                    checked={value || false}
                    onChange={(e) => onChange(e.target.checked)}
                  />
                )}
              />
            </ArcStack>
          )}
            
          <ArcButton.brand isLoading={isSubmitting} type='submit'> Create </ArcButton.brand>
        </ArcStack>
      </ArcDialog>
    </React.Fragment>
  );
};

export default CreateUserForm;